export const V4_ACCOUNT_MANAGER_SETTING = 'account_manager';

export const isV4ManagerFlagged = settings => {
  const value = settings?.[V4_ACCOUNT_MANAGER_SETTING];
  return value === true || value === 'true';
};

export const v4ManagerSettingValue = flagged => (flagged ? 'true' : 'false');

const lc = value => String(value ?? '').toLowerCase();

const field = (account, key) =>
  typeof account?.get === 'function' ? account.get(key) : account?.[key];

const toNode = account => {
  const id = field(account, 'id');
  if (!id) return null;
  const ownerId = field(account, 'v4OwnerId') ?? field(account, 'owner_id') ?? null;
  return {
    id: String(id),
    key: lc(id),
    ownerKey: ownerId ? lc(ownerId) : null,
    name: String(field(account, 'name') || ''),
    flagged: field(account, 'v4ManagerFlag') === true || isV4ManagerFlagged(field(account, 'settings')),
    archived: !!(field(account, 'archived') || field(account, 'archived_at')),
    deleted: !!(field(account, 'deleted') || field(account, 'deleted_at')),
    account,
  };
};

export function isV4ManagerIn(accounts, account) {
  const self = toNode(account);
  if (!self) return false;
  if (self.flagged) return true;
  const list = Array.isArray(accounts)
    ? accounts
    : typeof accounts?.toArray === 'function' ? accounts.toArray() : [];
  return list.some(a => {
    const n = toNode(a);
    return !!n && !n.deleted && n.key !== self.key && n.ownerKey === self.key;
  });
}

const byName = (a, b) =>
  a.name.localeCompare(b.name, undefined, {sensitivity: 'base', numeric: true});

export const V4_PATH_SEPARATOR = ' › ';

export function buildV4AccountTree(accounts, rootId, {rootName = ''} = {}) {
  const list = Array.isArray(accounts)
    ? accounts
    : typeof accounts?.toArray === 'function' ? accounts.toArray() : [];
  const rootKey = lc(rootId);

  const all = new Map();
  list.forEach(account => {
    const node = toNode(account);
    if (!node || node.deleted || all.has(node.key)) return;
    all.set(node.key, node);
  });

  const childrenByKey = new Map();
  all.forEach(node => {
    if (!node.ownerKey || node.ownerKey === node.key) return;
    if (!childrenByKey.has(node.ownerKey)) childrenByKey.set(node.ownerKey, []);
    childrenByKey.get(node.ownerKey).push(node);
  });
  childrenByKey.forEach(children => children.sort(byName));

  const root = all.get(rootKey) || (rootKey
    ? {id: String(rootId), key: rootKey, ownerKey: null, name: String(rootName || ''), flagged: false,
      archived: false, deleted: false, account: null}
    : null);

  // Keep only the root's walk: the list also has parents and siblings, and may cycle.
  const parentOf = new Map();
  const depthOf = new Map();
  const order = [];
  if (root) {
    depthOf.set(root.key, 0);
    const stack = [root];
    while (stack.length) {
      const node = stack.pop();
      order.push(node);
      const children = childrenByKey.get(node.key) || [];
      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        if (depthOf.has(child.key)) continue;
        depthOf.set(child.key, depthOf.get(node.key) + 1);
        parentOf.set(child.key, node);
        stack.push(child);
      }
    }
  }

  const inTree = id => depthOf.has(lc(id));
  const node = id => (inTree(id) ? (lc(id) === rootKey ? root : all.get(lc(id))) : null);
  const childrenOf = id => (inTree(id) ? childrenByKey.get(lc(id)) || [] : []);

  const descendantsOf = id => {
    if (!inTree(id)) return [];
    const out = [];
    const stack = [...childrenOf(id)].reverse();
    while (stack.length) {
      const next = stack.pop();
      out.push(next);
      stack.push(...[...childrenOf(next.id)].reverse());
    }
    return out;
  };

  const ancestorsOf = id => {
    const out = [];
    let parent = inTree(id) ? parentOf.get(lc(id)) : null;
    while (parent) {
      out.unshift(parent);
      parent = parentOf.get(parent.key);
    }
    return out;
  };

  const isManager = id => {
    const n = node(id);
    return !!n && (n.flagged || childrenOf(id).length > 0);
  };

  const pathOf = id => {
    const n = node(id);
    if (!n || n === root) return [];
    return [...ancestorsOf(id).slice(1), n];
  };

  return {
    root,
    rootId: root ? root.id : null,
    has: inTree,
    node,
    isRoot: id => !!root && lc(id) === rootKey,
    nodes: () => [...order],
    childrenOf,
    descendantsOf,
    ancestorsOf,
    parentOf: id => (inTree(id) ? parentOf.get(lc(id)) || null : null),
    depthOf: id => (inTree(id) ? depthOf.get(lc(id)) : -1),
    isManager,
    isDirect: id => inTree(id) && parentOf.get(lc(id)) === root,
    branchOf: id => pathOf(id)[0] || null,
    pathOf,
    pathLabel: (id, separator = V4_PATH_SEPARATOR) =>
      pathOf(id).map(n => n.name).join(separator),
  };
}

export function v4AccountPickerItems(tree, {
  t,
  includeRoot = false,
  managersOnly = false,
  grouped = false,
  filter = () => true,
  keepIds = [],
  extras = [],
} = {}) {
  if (!tree?.root) return [];
  const translate = typeof t === 'function'
    ? t
    : (key, opts) => (typeof opts === 'string'
      ? opts
      : String(opts?.defaultValue || key).replace(/\{(\w+)\}/g, (_, name) => opts?.[name] ?? ''));
  const keep = new Set((keepIds || []).filter(Boolean).map(lc));

  const offered = tree.nodes().filter(n => {
    // `keepIds` pass every filter, or an edit form would blank its current value.
    if (keep.has(n.key)) return true;
    if (tree.isRoot(n.id) && !includeRoot) return false;
    if (managersOnly && !tree.isRoot(n.id) && !tree.isManager(n.id)) return false;
    return filter(n);
  });

  const toItem = n => {
    const isRoot = tree.isRoot(n.id);
    const branch = tree.branchOf(n.id);
    const managed = !!branch && tree.isManager(branch.id);
    return {
      value: n.id,
      label: n.name || n.id,
      group: grouped
        ? (managed
          ? translate('viaAccountManager', {
            name: branch.name,
            defaultValue: 'Account manager: {name}',
          })
          : translate('directAccounts', 'Direct accounts'))
        : undefined,
      object: {
        path: isRoot ? '' : tree.pathLabel(n.id),
        depth: tree.depthOf(n.id),
        manager: isRoot || tree.isManager(n.id),
        root: isRoot,
        direct: tree.isDirect(n.id),
        archived: n.archived,
        branchId: branch ? branch.id : null,
      },
      _branchName: managed ? branch.name : '',
      _managed: managed,
    };
  };

  const seen = new Set();
  const outside = (extras || []).filter(a => {
    const key = lc(field(a, 'id'));
    if (!key || tree.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map(a => ({
    value: String(field(a, 'id')),
    label: String(field(a, 'name') || field(a, 'id')),
    group: grouped ? translate('other', 'Other') : undefined,
    object: {path: '', depth: 1, manager: false, root: false, direct: false,
      archived: !!field(a, 'archived'), branchId: null},
  }));

  const items = offered.map(toItem);
  if (!grouped) return [...items.map(({_branchName, _managed, ...item}) => item), ...outside];

  // Autocomplete groups consecutive options, so order by group: own accounts, then each branch.
  const own = items.filter(i => !i._managed);
  const branches = new Map();
  items.filter(i => i._managed).forEach(i => {
    const key = i.object.branchId;
    if (!branches.has(key)) branches.set(key, {name: i._branchName, items: []});
    branches.get(key).items.push(i);
  });
  const sortedBranches = [...branches.values()]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base', numeric: true}));
  return [...own, ...sortedBranches.flatMap(b => b.items)]
    .map(({_branchName, _managed, ...item}) => item)
    .concat(outside);
}

/**
 * The accounts above `id`, nearest first: its owner on up to the root, then the
 * session's own account (`ownId`). An entered sub-account manager's tree is
 * rooted beneath it; the accounts in between are not listed there, so they are
 * skipped. Empty for the root and outside the tree, whose chains are unknown.
 */
export function v4AccountsAbove(tree, id, ownId) {
  if (!tree?.root || !tree.has(id) || tree.isRoot(id)) return [];
  const above = tree.ancestorsOf(id).reverse().map(n => n.id);
  if (ownId && !above.some(a => lc(a) === lc(ownId))) above.push(String(ownId));
  return above;
}

export function v4SubtreeDeviceTotals(tree, counts) {
  const totals = new Map();
  if (tree?.root) {
    const own = n => +(counts?.[n.id]?.total || 0);
    // Reverse pre-order visits every child before its parent.
    tree.nodes().reverse().forEach(n => {
      const below = tree.childrenOf(n.id).reduce((sum, c) => sum + (totals.get(c.key) || 0), 0);
      totals.set(n.key, own(n) + below);
    });
  }
  return id => totals.get(lc(id)) || 0;
}
